// Scroll-to-bottom button functionality

import { getCurrentResearchId } from '../state/research.js';
import { showShareModal } from './research-history.js';
import { showSnackbar } from './snackbar.js';

// Function to update button position relative to search box on small screens
export function updateScrollButtonPosition() {
    if (window.innerWidth > 650) return; // Only on small screens
    
    const scrollToBottomBtn = document.getElementById('scrollToBottomBtn');
    const searchBox = document.getElementById('searchBox');
    
    if (!scrollToBottomBtn || !searchBox) return;
    
    // Check if search box is bottom-positioned
    if (searchBox.classList.contains('bottom-positioned')) {
        const searchBoxRect = searchBox.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const spacing = 12; // Space between button and search box
        
        // Position scroll button just above the search box; share button above scroll button
        const bottomPosition = windowHeight - searchBoxRect.top + spacing;
        const shareGap = 56; // 44px scroll button height + 12px gap
        scrollToBottomBtn.style.bottom = `${bottomPosition}px`;
        const shareBtn = document.getElementById('shareResearchBtn');
        if (shareBtn) shareBtn.style.bottom = `${bottomPosition + shareGap}px`;
    } else {
        // Reset to default position when not bottom-positioned
        scrollToBottomBtn.style.bottom = '';
        const shareBtn = document.getElementById('shareResearchBtn');
        if (shareBtn) shareBtn.style.bottom = '';
    }
}

function checkScrollPosition() {
    const scrollToBottomBtn = document.getElementById('scrollToBottomBtn');
    if (!scrollToBottomBtn) return;
    
    // Calculate if user is at the bottom of the page
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollBottom = scrollTop + windowHeight;
    
    // Check if there's actually scrollable content (with threshold to account for rounding)
    // Only show button if there's scrollable content AND we're not at the bottom
    const hasScrollableContent = documentHeight > windowHeight + 100; // 100px threshold for iOS Safari quirks
    const isAtBottom = scrollBottom >= documentHeight - 50;
    
    if (!hasScrollableContent || isAtBottom) {
        scrollToBottomBtn.classList.remove('show');
    } else {
        scrollToBottomBtn.classList.add('show');
    }
}

function initializeScrollToBottomButton() {
    const scrollToBottomBtn = document.getElementById('scrollToBottomBtn');
    if (!scrollToBottomBtn) {
        console.warn('[Scroll] scrollToBottomBtn not found');
        return;
    }
    
    // Handle scroll to bottom button click
    scrollToBottomBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Calculate the maximum scroll position
        const scrollHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        );
        
        // Scroll to the bottom
        window.scrollTo({ 
            top: scrollHeight, 
            behavior: 'smooth' 
        });
        
        console.log('[Scroll] Clicked scroll to bottom, scrollHeight:', scrollHeight, 'current scroll:', window.pageYOffset);
    });
    
    // Check scroll position on scroll events (debounced)
    let scrollTimeout;
    function handleScroll() {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(checkScrollPosition, 100);
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', () => {
        handleScroll();
        updateScrollButtonPosition(); // Update position on resize
    });
    
    // Update button position periodically and on textarea changes
    const updateInterval = setInterval(() => {
        updateScrollButtonPosition();
    }, 200); // Check every 200ms
    
    // Clean up interval when page unloads (optional)
    window.addEventListener('beforeunload', () => {
        clearInterval(updateInterval);
    });
    
    // Initial check after a short delay to ensure DOM is fully loaded
    setTimeout(() => {
        checkScrollPosition();
        updateScrollButtonPosition();
    }, 100);
}

function initializeShareResearchButton() {
    const shareBtn = document.getElementById('shareResearchBtn');
    if (!shareBtn) return;
    shareBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Use pre-bundle state when available (e.g. after loading from research history)
        const researchId = (window.bms && typeof window.bms.getCurrentResearchId === 'function')
            ? window.bms.getCurrentResearchId()
            : getCurrentResearchId();
        if (researchId) {
            showShareModal(researchId);
        } else {
            showSnackbar('Start a search to share your research topic.', 'info', 'info', 3000);
        }
    });
}

export function initScrollToBottom() {
    // Initialize on DOM ready
    let buttonInitAttempts = 0;
    const maxInitAttempts = 50; // Maximum 5 seconds (50 * 100ms)
    
    function waitForButtonAndInitialize() {
        const scrollToBottomBtn = document.getElementById('scrollToBottomBtn');
        if (scrollToBottomBtn) {
            initializeScrollToBottomButton();
            initializeShareResearchButton();
        } else if (buttonInitAttempts < maxInitAttempts) {
            // Button not found, try again after a short delay
            buttonInitAttempts++;
            setTimeout(waitForButtonAndInitialize, 100);
        } else {
            console.warn('[Scroll] scrollToBottomBtn not found after max attempts');
            const shareBtn = document.getElementById('shareResearchBtn');
            if (shareBtn) initializeShareResearchButton();
        }
    }
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', waitForButtonAndInitialize);
    } else {
        // DOM is already loaded, but button might not be yet (if script loads before button HTML)
        waitForButtonAndInitialize();
    }
}
